import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { fetchSecurityLogins } from "@admin/services/adminApi";
import { DataTable, Pill } from "@admin/components/Primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paginator, PAGE_SIZE } from "@admin/components/security/Paginator";
import { AdminPicker, ALL_ADMINS, adminParam } from "@admin/components/security/AdminPicker";
import { fmtDt } from "@admin/components/security/format";

export function LoginsSection({ canManageAdmins, meId }) {
  const [flag, setFlag] = useState("all");
  const [admin, setAdmin] = useState(ALL_ADMINS);
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ total: 0, items: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await fetchSecurityLogins({
        suspicious: flag === "all" ? undefined : flag,
        user_id: canManageAdmins ? adminParam(admin, meId) : undefined,
        skip, limit: PAGE_SIZE,
      });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load sign-ins"); }
    setBusy(false);
  }, [flag, admin, skip, canManageAdmins, meId]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "created_at", label: "When", render: (r) => <span className="text-xs text-slate-600">{fmtDt(r.created_at)}</span> },
    {
      key: "email", label: "Admin",
      render: (r) => (
        <div className="min-w-[180px]">
          <p className="font-bold text-[#0A2540] text-sm">{r.email || "—"}</p>
          <code className="text-[10px] text-slate-400">{(r.user_id || "").slice(0, 12)}</code>
        </div>
      ),
    },
    { key: "ip", label: "IP", render: (r) => <code className="text-xs">{r.ip || "—"}</code> },
    { key: "ua_family", label: "Device", render: (r) => <span className="text-xs">{r.ua_family || "unknown"}</span> },
    {
      key: "suspicious", label: "Flag",
      render: (r) => r.suspicious
        ? <Pill color="rose"><ShieldAlert size={10} /> {r.suspicious_reason || "suspicious"}</Pill>
        : <Pill color="emerald">OK</Pill>,
    },
  ];

  return (
    <section className="mt-8" data-testid="security-logins-section">
      <div className="flex flex-wrap justify-between items-end gap-2 mb-3">
        <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400">Recent sign-ins</h2>
        <div className="flex flex-wrap gap-2">
          {canManageAdmins && <AdminPicker value={admin} meId={meId} testid="logins-filter-admin" onChange={(v) => { setAdmin(v); setSkip(0); }} />}
          <Select value={flag} onValueChange={(v) => { setFlag(v); setSkip(0); }}>
            <SelectTrigger className="w-[170px] h-9 text-xs" data-testid="logins-filter-flag"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="logins-flag-all">All sign-ins</SelectItem>
              <SelectItem value="true" data-testid="logins-flag-suspicious">Suspicious only</SelectItem>
              <SelectItem value="false" data-testid="logins-flag-ok">OK only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DataTable testid="logins-table" columns={columns} rows={data.items} empty={busy ? "Loading…" : "No sign-in activity for this filter"} />
      <Paginator skip={skip} total={data.total} onChange={setSkip} testid="logins-pager" busy={busy} />
    </section>
  );
}
